import type { FC} from 'react';
import { useCallback, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Button, CircularProgress, Unstable_Grid2 as Grid, TextField, Box, Typography } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { z as zod } from 'zod';

import type { MeetingType } from '@/types/meeting';
import type { Meeting, MeetingFile } from '@/types/v2/meeting';
import { config } from '@/config';
import { paths } from '@/paths';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import ProgressButton from '@/components/common/buttons/progress-button';
import SubLayoutContainer from '@/components/sub-layout-container';
import ProfessionalDialog from '@/components/common/professional-dialog';
import { getPresignedUploadUrl, uploadFileToPresignedUrl } from '@/utils/s3';

import { toTitleCase } from '../../../utils/to-title-case';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MeetingRoom } from '@mui/icons-material';

// Extend dayjs with UTC plugin
dayjs.extend(utc);
import { ChartBar, PresentationChart } from '@phosphor-icons/react';

const schema = zod
  .object({
    community_id: zod.union([zod.string(), zod.number()]).transform((v) => String(v)),
    meeting_type_id: zod.number().min(1, 'Please select a meeting type'),
    held_on_date: zod
      .union([zod.string(), zod.date(), zod.null()])
      .refine((v) => v != null && v !== '' && dayjs(v).isValid(), 'Please select a date'),
    held_on_time: zod
      .union([zod.string(), zod.date(), zod.null()])
      .refine((v) => v != null && v !== '' && (typeof v !== 'string' || v.trim() !== ''), 'Please select a time'),
    agenda: zod.string().min(10, 'Meeting agenda must be at least 10 characters'),
  })
  .refine(
    (data) => {
      if (!data.held_on_date || !data.held_on_time) return true; // Let required checks handle empty
      const date = dayjs(data.held_on_date);
      const time = dayjs(data.held_on_time);
      const combined = date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
      return combined.isAfter(dayjs().add(1, 'hour'), 'minute');
    },
    { message: 'Meeting must be scheduled at least 1 hour in advance', path: ['held_on_time'] }
  );

type Values = zod.infer<typeof schema>;
type FileWithObject = File & { _objectUrl?: string };

interface CreateMeetingDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateMeetingDialog: FC<CreateMeetingDialogProps> = (props) => {
  const { open, onClose } = props;
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[] | null>(null);
  const [documentFile, setDocumentFile] = useState<FileWithObject | null>(null);
  const { user, community } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const defaultValues: Values = {
    meeting_type_id: 0,
    held_on_date: null as any,
    held_on_time: null as any,
    agenda: '',
    community_id: community?.id || '',
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    register,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const createMeetingRequest = useMutation({
    mutationFn: async (payload: { community_id: string; meeting_type_id: number; agenda: string; held_on: string; files?: MeetingFile[] }) => {
      const response = await axios.post<{ meeting: Meeting; error?: string }>(
        `${config.site.api}/api/v2/communities/${community?.id}/meetings`,
        payload
      );
      return response.data;
    },
  });

  const onInvalid = () => {
    toast.error('Please fix the form errors before submitting');
  };

  const onSubmit = async (values: Values) => {
    try {
      const date = dayjs(values.held_on_date);
      const time = dayjs(values.held_on_time);
      const held_on = date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

      let files: MeetingFile[] | undefined;
      if (documentFile) {
        const { url, key } = await getPresignedUploadUrl(documentFile);
        await uploadFileToPresignedUrl(url, documentFile);
        files = [{ key, file_name: documentFile.name }];
      }
      const response = await createMeetingRequest.mutateAsync({
        community_id: values.community_id,
        meeting_type_id: values.meeting_type_id,
        agenda: values.agenda,
        held_on,
        ...(files?.length ? { files } : {}),
      });
      if (response.error) {
        toast.error('Failed to create meeting');
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      navigate(paths.meetings.meeting(response?.meeting?.uuid ?? response?.meeting?.id ?? ''));
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Unable to create meeting');
    }
  };

  const handleClose = useCallback((): void => {
    setDocumentFile(null);
    onClose();
    reset();
  }, [onClose, reset]);

  useEffect(() => {
    const fetchMeetingTypes = async () => {
      try {
        const response = await axios.get(`${config.site.api}/api/v1/meetings/types`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        const data = response.data;
        setMeetingTypes(data);
      } catch (error) {
        console.error(error);
        setMeetingTypes([]);
        toast.error('Unable to fetch meeting types');
      }
    };

    fetchMeetingTypes();
  }, []);

  useEffect(() => {
    setValue('community_id', community?.id || '');
  }, [community, setValue]);

  // Auto-select first meeting type when options load
  useEffect(() => {
    if (meetingTypes?.length && open) {
      setValue('meeting_type_id', meetingTypes[0].id);
    }
  }, [meetingTypes, open, setValue]);

  return (
    <ProfessionalDialog
      actions={null}
      autoHeight
      contentSx={{ pt: 2 }}
      icon={<PresentationChart />}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle="Schedule a new meeting for your community"
      title="Create Meeting"
    >
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <SubLayoutContainer>
            <Grid md={6} xs={12}>
              <Controller
                control={control}
                name="meeting_type_id"
                render={({ field: { onChange, value } }) => {
                  const selectedOption = meetingTypes?.find((mt) => mt.id === value) ?? null;
                  return (
                  <Autocomplete
                    fullWidth
                    getOptionLabel={(option) => toTitleCase(option.name)}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    onChange={(_, newValue) => {
                      onChange(newValue?.id ?? 0);
                    }}
                    options={meetingTypes ?? []}
                    value={selectedOption}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(errors.meeting_type_id)}
                        helperText={errors.meeting_type_id?.message}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {!meetingTypes ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        label="Meeting Type"
                      />
                    )}
                    size="small"
                  />
                  );
                }}
              />
            </Grid>

            <Grid md={6} xs={12}>
              <Controller
                control={control}
                name="held_on_date"
                render={({ field }) => (
                  <DatePicker
                    label="Meeting Date"
                    minDate={dayjs()}
                    onChange={(newValue) => {
                      field.onChange(newValue ? dayjs(newValue).toDate() : null);
                      trigger(['held_on_date', 'held_on_time']);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.held_on_date),
                        helperText: errors.held_on_date?.message,
                        size: 'small',
                      },
                    }}
                    value={field.value ? dayjs(field.value) : null}
                  />
                )}
              />
            </Grid>

            <Grid md={6} xs={12}>
              <Controller
                control={control}
                name="held_on_time"
                render={({ field }) => (
                  <TimePicker
                    label="Meeting Time"
                    minutesStep={15}
                    onChange={(newValue) => {
                      field.onChange(newValue ? dayjs(newValue).toDate() : null);
                      trigger(['held_on_date', 'held_on_time']);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.held_on_time),
                        helperText: errors.held_on_time?.message || 'Select a time (e.g. 2:00 PM)',
                        size: 'small',
                      },
                    }}
                    value={field.value ? dayjs(field.value) : null}
                    ampm
                  />
                )}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                {...register('agenda')}
                error={Boolean(errors.agenda)}
                fullWidth
                helperText={errors.agenda?.message || 'Describe the meeting agenda and topics to discuss'}
                label="Meeting Agenda"
                multiline
                rows={4}
                size="small"
                placeholder="Enter meeting agenda, topics to discuss, or other relevant information..."
              />
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Meeting document (optional)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<ChartBar />}
              >
                {documentFile ? documentFile.name : 'Choose file'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.txt,image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setDocumentFile(f ? (f as FileWithObject) : null);
                  }}
                />
              </Button>
              {documentFile ? (
                <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                  {documentFile.name}
                </Typography>
              ) : null}
            </Grid>
        </SubLayoutContainer>

        <Box display="flex" gap={2} justifyContent="flex-end" sx={{ pt: 2 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          <ProgressButton
            disabled={isSubmitting}
            loading={isSubmitting}
            loadingText="Creating..."
            onClick={() => handleSubmit(onSubmit, onInvalid)()}
            size="medium"
            text="Create"
            type="button"
            variant="contained"
          />
        </Box>
      </form>
    </ProfessionalDialog>
  );
};

export default CreateMeetingDialog;
